import type { OpenAPIV3_1 } from "openapi-types";

// ---------------------------------------------------------------------------
// Equation excel_client document extension
// ---------------------------------------------------------------------------

export const SPECKLE_INTEGRATION_TYPE = "speckle";

type SpeckleIntegrationType = typeof SPECKLE_INTEGRATION_TYPE;

export type EquationExcelClientProfile = {
  profile_name: string;
  core_endpoints: string[];
  type?: SpeckleIntegrationType | null;
  receive_speckle_endpoint?: string | null;
  send_speckle_endpoint?: string | null;
  disabled: boolean;
  hidden: boolean;
};

export type EquationExcelClientApp = {
  app_name: string;
  package_name?: string | null;
  package_version?: string | null;
  route?: string | null;
  profiles: EquationExcelClientProfile[];
};

export type EquationExcelClientMetadata = {
  apps: EquationExcelClientApp[];
};

export type VisibleExcelProfile = {
  app: EquationExcelClientApp;
  profile: EquationExcelClientProfile;
};

export type Equation = {
  excel_client?: EquationExcelClientMetadata;
};

// ---------------------------------------------------------------------------
// Equation field-level extensions (style, unit)
// ---------------------------------------------------------------------------

export type JsonSchemaType =
  | OpenAPIV3_1.ArraySchemaObjectType
  | OpenAPIV3_1.NonArraySchemaObjectType
  | (OpenAPIV3_1.ArraySchemaObjectType | OpenAPIV3_1.NonArraySchemaObjectType)[];

export type BaseStyle = {
  disabled?: boolean;
  hidden?: boolean;
  css?: string;
};

export type CardStyle = BaseStyle & {
  title: string;
  subtitle?: string;
  icon?: string;
  columns?: number;
  background_color?: string;
  max_inputs?: number;
  min_inputs?: number;
  image_url?: string;
};

export type FieldStyle = BaseStyle & {
  editable?: boolean;
  cols?: number;
  offset?: number;
  type?: string;
  precision?: number;
};

export type SectionStyle = BaseStyle & {
  title: string;
  description: string;
};

export type Style = CardStyle & FieldStyle & SectionStyle;

export type AffineConversion = {
  is_multiplicative: boolean;
  offset: number;
  scale: number;
};

export type FormattedUnit = {
  html: string;
  latex: string;
  pretty_print: string;
};

export type Unit = {
  base: string;
  conversions: {
    base: AffineConversion;
    metric: AffineConversion;
    imperial: AffineConversion;
  };
  formatted: {
    base: FormattedUnit;
    metric: FormattedUnit;
    imperial: FormattedUnit;
  };
  metric: string;
  imperial: string;
};

// ---------------------------------------------------------------------------
// Extended OpenAPI document tree.
// ---------------------------------------------------------------------------

export type ExtendedDocument<T extends {} = {}> = Omit<OpenAPIV3_1.Document<T>, "paths" | "components"> & {
  paths: ExtendedPathsObject<T>;
  components?: ExtendedComponentsObject;
  equation?: Equation;
};

export type ExtendedPathsObject<T extends {} = {}, P extends {} = {}> = {
  [pattern: string]: (ExtendedPathItemObject<T> & P) | undefined;
};

export type ExtendedPathItemObject<T extends {} = {}> = Omit<OpenAPIV3_1.PathItemObject<T>, OpenAPIV3_1.HttpMethods> & {
  [method in OpenAPIV3_1.HttpMethods]?: ExtendedOperationObject<T>;
};

export type ExtendedOperationObject<T extends {} = {}> = Omit<
  OpenAPIV3_1.OperationObject<T>,
  "requestBody" | "responses"
> & {
  requestBody?: ExtendedReferenceObject | ExtendedRequestBodyObject;
  responses: ExtendedResponsesObject;
};

export type ExtendedSchemaObject = Omit<
  OpenAPIV3_1.SchemaObject,
  "additionalProperties" | "properties" | "allOf" | "oneOf" | "anyOf" | "not" | "items"
> & {
  additionalProperties?: boolean | ExtendedReferenceObject | ExtendedSchemaObject;
  properties?: {
    [name: string]: ExtendedReferenceObject | ExtendedSchemaObject;
  };
  allOf?: (ExtendedReferenceObject | ExtendedSchemaObject)[];
  oneOf?: (ExtendedReferenceObject | ExtendedSchemaObject)[];
  anyOf?: (ExtendedReferenceObject | ExtendedSchemaObject)[];
  not?: ExtendedReferenceObject | ExtendedSchemaObject;
  items?: ExtendedReferenceObject | ExtendedSchemaObject;
  style?: Style;
  unit?: Unit;
};

export type ExtendedReferenceObject = OpenAPIV3_1.ReferenceObject & {
  style?: Style;
  unit?: Unit;
};

export type ExtendedMediaTypeObject = Omit<OpenAPIV3_1.MediaTypeObject, "schema"> & {
  schema?: ExtendedReferenceObject | ExtendedSchemaObject;
};

export type ExtendedRequestBodyObject = Omit<OpenAPIV3_1.RequestBodyObject, "content"> & {
  content: {
    [media: string]: ExtendedMediaTypeObject;
  };
};

export type ExtendedResponsesObject = {
  [code: string]: ExtendedReferenceObject | ExtendedResponseObject;
};

export type ExtendedResponseObject = Omit<OpenAPIV3_1.ResponseObject, "content"> & {
  content: {
    [media: string]: ExtendedMediaTypeObject;
  };
};

export type ExtendedComponentsObject = Omit<OpenAPIV3_1.ComponentsObject, "schemas"> & {
  schemas?: {
    [key: string]: ExtendedReferenceObject | ExtendedSchemaObject;
  };
};

export type ResolvedExtendedOperationObject<T extends {} = {}> = Omit<
  OpenAPIV3_1.OperationObject<T>,
  "requestBody" | "responses"
> & {
  requestBody?: ResolvedExtendedRequestBodyObject;
  responses: ResolvedExtendedResponsesObject;
};

export type ResolvedExtendedSchemaObject = Omit<
  OpenAPIV3_1.SchemaObject,
  "additionalProperties" | "properties" | "allOf" | "oneOf" | "anyOf" | "not" | "items"
> & {
  additionalProperties?: boolean | ResolvedExtendedSchemaObject;
  properties?: {
    [name: string]: ResolvedExtendedSchemaObject;
  };
  allOf?: ResolvedExtendedSchemaObject[];
  oneOf?: ResolvedExtendedSchemaObject[];
  anyOf?: ResolvedExtendedSchemaObject[];
  not?: ResolvedExtendedSchemaObject;
  items?: ResolvedExtendedSchemaObject;
  style?: Style;
  unit?: Unit;
} & {
  value?: any;
  [key: string]: any;
};

export type ResolvedExtendedMediaTypeObject = Omit<OpenAPIV3_1.MediaTypeObject, "schema"> & {
  schema?: ResolvedExtendedSchemaObject;
};

export type ResolvedExtendedRequestBodyObject = Omit<OpenAPIV3_1.RequestBodyObject, "content"> & {
  content: {
    [media: string]: ResolvedExtendedMediaTypeObject;
  };
};

export type ResolvedExtendedResponsesObject = {
  [code: string]: ResolvedExtendedResponseObject;
};

export type ResolvedExtendedResponseObject = Omit<OpenAPIV3_1.ResponseObject, "content"> & {
  content: {
    [media: string]: ResolvedExtendedMediaTypeObject;
  };
};

export type SchemaInfo = OpenAPIV3_1.InfoObject;
